// src/pages/OurStore.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import Meta from "../components/Meta";
import Container from "../components/Container";
import { Link, useLocation, useNavigate } from "react-router-dom";
import "./styles/ourstore.css";
import { IoIosHeartEmpty } from "react-icons/io";
import { TbAdjustmentsHorizontal } from "react-icons/tb";
import { IoClose } from "react-icons/io5";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import OfflineOverlay from "../components/OfflineOverlay";
import {
  getAllProducts,
  addToWishlists,
} from "../app/features/products/productSlice";
import { getAllColors } from "../app/features/color/colorSlice";
import {
  getAllBrands,
  selectBrands,
  selectBrandLoading,
} from "../app/features/brand/brandSlice";
import {
  addProdToCart,
  getUserCart as fetchUserCart,
} from "../app/features/user/userSlice";
import {
  fetchCategories,
} from "../app/features/category/categorySlice";

const GUEST_CART_KEY = "kupto_guest_cart_v1";
const SEARCH_LIMIT = 5000;

const shuffleArray = (arr) => {
  const a = Array.isArray(arr) ? [...arr] : [];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

const normalize = (v) => String(v ?? "").toLowerCase().trim();

const productHasCategoryId = (product, wantedId) => {
  if (!wantedId) return true;
  const cats = Array.isArray(product?.category) ? product.category : [];
  return cats.some((c) => {
    if (typeof c === "object" && c) return String(c?._id) === String(wantedId);
    return String(c) === String(wantedId);
  });
};

const productHasColorId = (product, colorIds = []) => {
  if (!Array.isArray(colorIds) || colorIds.length === 0) return true;
  const colors = Array.isArray(product?.color) ? product.color : [];
  const wanted = new Set(colorIds.map((id) => String(id)));
  return colors.some((c) => {
    if (typeof c === "object" && c) return wanted.has(String(c?._id));
    return wanted.has(String(c));
  });
};

const productHasTag = (product, tags = []) => {
  if (!Array.isArray(tags) || tags.length === 0) return true;
  const pTags = Array.isArray(product?.tags) ? product.tags.map((t) => normalize(t)) : [];
  return tags.some((t) => pTags.includes(normalize(t)));
};

const productMatchesPrice = (product, minPrice, maxPrice) => {
  const price = Number(product?.discountedPrice ?? product?.price ?? 0) || 0;

  if (minPrice !== "" && minPrice != null && price < Number(minPrice)) return false;
  if (maxPrice !== "" && maxPrice != null && price > Number(maxPrice)) return false;

  return true;
};

const sortProductsLocal = (list, sortBy) => {
  const arr = Array.isArray(list) ? [...list] : [];

  if (!sortBy) return arr;

  if (sortBy === "price") {
    return arr.sort(
      (a, b) =>
        (Number(a?.discountedPrice ?? a?.price ?? 0) || 0) -
        (Number(b?.discountedPrice ?? b?.price ?? 0) || 0)
    );
  }

  if (sortBy === "-price") {
    return arr.sort(
      (a, b) =>
        (Number(b?.discountedPrice ?? b?.price ?? 0) || 0) -
        (Number(a?.discountedPrice ?? a?.price ?? 0) || 0)
    );
  }

  if (sortBy === "-totalrating") {
    return arr.sort(
      (a, b) => (Number(b?.totalrating || 0) || 0) - (Number(a?.totalrating || 0) || 0)
    );
  }

  if (sortBy === "-createdAt") {
    return arr.sort((a, b) => {
      const da = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const db = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      return db - da;
    });
  }

  return arr;
};

/* =========================
   ✅ SKELETONS + IMAGE PLACEHOLDER
========================= */
const ImageWithPlaceholder = ({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);

  return (
    <div className="os-imgSlot">
      {!loaded && !failed && (
        <div className="os-imgPlaceholder" aria-label="Loading image">
          <div className="os-imgIconSpin" aria-hidden="true">
            <AiOutlineLoading3Quarters />
          </div>
          <div className="os-imgHint">Loading...</div>
        </div>
      )}

      {failed && (
        <div className="os-imgPlaceholder os-imgPlaceholder--fail">
          <div className="os-imgHint">No image</div>
        </div>
      )}

      <img
        className={`os-prodImg ${loaded ? "is-loaded" : ""}`}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => setFailed(true)}
        draggable="false"
      />
    </div>
  );
};

const SkeletonGrid = ({ count = 12 }) => {
  return (
    <div className="os-skelGrid" aria-label="Loading products">
      {Array.from({ length: count }).map((_, i) => (
        <div className="os-skelCard" key={i}>
          <div className="os-skelTop">
            <div className="os-skelMenu os-skel" />
            <div className="os-skelImg os-skel" />
          </div>

          <div className="os-skelBody">
            <div className="os-skelTitle os-skel" />
            <div className="os-skelPriceRow">
              <div className="os-skelPrice os-skel" />
              <div className="os-skelPriceSmall os-skel" />
            </div>
            <div className="os-skelStars os-skel" />
          </div>
        </div>
      ))}
    </div>
  );
};

const SidebarSkeleton = () => {
  return (
    <>
      <div className="os-panel">
        <div className="os-panel-head">SHOP BY</div>

        <div className="os-block">
          <div className="os-block-title">CATEGORY</div>
          <div className="os-skelList">
            {Array.from({ length: 7 }).map((_, i) => (
              <div className="os-skelRow" key={i}>
                <div className="os-skelCheck os-skel" />
                <div className="os-skelLine os-skel" />
              </div>
            ))}
          </div>
        </div>

        <div className="os-sep" />

        <div className="os-block">
          <div className="os-block-title">PRICE</div>
          <div className="os-priceRow">
            <div className="os-skelInp os-skel" />
            <div className="os-skelInp os-skel" />
            <div className="os-skelBtn os-skel" />
          </div>
        </div>

        <div className="os-sep" />

        <div className="os-block">
          <div className="os-block-title">BRAND</div>
          <div className="os-skelList">
            {Array.from({ length: 6 }).map((_, i) => (
              <div className="os-skelRow" key={i}>
                <div className="os-skelCheck os-skel" />
                <div className="os-skelLine os-skel" />
              </div>
            ))}
          </div>
        </div>

        <div className="os-sep" />

        <div className="os-block">
          <div className="os-block-title">TAGS</div>
          <div className="os-skelList">
            {Array.from({ length: 4 }).map((_, i) => (
              <div className="os-skelRow" key={i}>
                <div className="os-skelCheck os-skel" />
                <div className="os-skelLine os-skel" />
              </div>
            ))}
          </div>
        </div>

        <div className="os-sep" />

        <div className="os-block">
          <div className="os-block-title">COLOR</div>
          <div className="os-skelColorGrid">
            {Array.from({ length: 16 }).map((_, i) => (
              <div className="os-skelColor os-skel" key={i} />
            ))}
          </div>
        </div>
      </div>

      <div className="os-panel">
        <div className="os-panel-head">BEST SELLERS</div>
        <div className="os-bestWrap">
          {Array.from({ length: 3 }).map((_, i) => (
            <div className="os-skelBestItem" key={i}>
              <div className="os-skelBestImg os-skel" />
              <div className="os-skelBestInfo">
                <div className="os-skelBestTitle os-skel" />
                <div className="os-skelBestStars os-skel" />
                <div className="os-skelBestPrice os-skel" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const AppDrawerSkeleton = () => {
  return (
    <div className="os-appDrawerBody">
      <div className="os-appBlock">
        <div className="os-appBlockTitle">Category</div>
        <div className="os-skelList os-skelList--app">
          {Array.from({ length: 8 }).map((_, i) => (
            <div className="os-skelRow" key={i}>
              <div className="os-skelCheck os-skel" />
              <div className="os-skelLine os-skel" />
            </div>
          ))}
        </div>
      </div>

      <div className="os-sep" />

      <div className="os-appBlock">
        <div className="os-appBlockTitle">Price</div>
        <div className="os-priceRow os-priceRow--app">
          <div className="os-skelInp os-skel" />
          <div className="os-skelInp os-skel" />
          <div
            className="os-skelBtn os-skel"
            style={{ gridColumn: "1 / -1" }}
          />
        </div>
      </div>

      <div className="os-sep" />

      <div className="os-appBlock">
        <div className="os-appBlockTitle">Brand</div>
        <div className="os-skelList os-skelList--app">
          {Array.from({ length: 7 }).map((_, i) => (
            <div className="os-skelRow" key={i}>
              <div className="os-skelCheck os-skel" />
              <div className="os-skelLine os-skel" />
            </div>
          ))}
        </div>
      </div>

      <div className="os-sep" />

      <div className="os-appBlock">
        <div className="os-appBlockTitle">Tags</div>
        <div className="os-skelList os-skelList--app">
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="os-skelRow" key={i}>
              <div className="os-skelCheck os-skel" />
              <div className="os-skelLine os-skel" />
            </div>
          ))}
        </div>
      </div>

      <div className="os-sep" />

      <div className="os-appBlock">
        <div className="os-appBlockTitle">Color</div>
        <div className="os-skelColorGrid os-skelColorGrid--app">
          {Array.from({ length: 52 }).map((_, i) => (
            <div className="os-skelColor os-skel" key={i} />
          ))}
        </div>
      </div>

      <div className="os-appDrawerFoot">
        <div className="os-skelBtn os-skel" />
        <div className="os-skelBtn os-skel" />
      </div>
    </div>
  );
};

const OurStore = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const urlParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const urlSearch = useMemo(
    () => (urlParams.get("search") || "").trim(),
    [urlParams]
  );

  const urlCategory = useMemo(
    () => (urlParams.get("category") || "").trim(),
    [urlParams]
  );

  const urlBrand = useMemo(
    () => (urlParams.get("brand") || "").trim(),
    [urlParams]
  );

  const selectedCategory = urlCategory || "";

  const [isApp, setIsApp] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 768px)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsApp(Boolean(e.matches));

    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    setIsApp(mq.matches);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const authState = useSelector((state) => state?.auth || {});
  const isLoggedIn = Boolean(
    authState?.user?.token || localStorage.getItem("token")
  );

  const {
    products,
    isLoading,
    isError,
    message,
    total: totalFromApi,
  } = useSelector((state) => state.product || {});

  const categories = useSelector((state) => state.category?.categories || []);

  const brandsFromStore = useSelector(selectBrands);
  const brandsLoading = useSelector(selectBrandLoading);

  const brands = useMemo(() => {
    const list = Array.isArray(brandsFromStore) ? brandsFromStore : [];
    return list
      .filter((b) => b?.isActive !== false)
      .slice()
      .sort((a, b) =>
        String(a?.title || "").localeCompare(String(b?.title || ""))
      );
  }, [brandsFromStore]);

  const colors = useSelector((state) => {
    const a = state?.colors?.colors;
    const b = state?.color?.colors;
    return (Array.isArray(a) ? a : null) ?? (Array.isArray(b) ? b : null) ?? [];
  });

  const [sortBy, setSortBy] = useState("");
  const [perPage, setPerPage] = useState(100);
  const [selectedBrandTitle, setSelectedBrandTitle] = useState(urlBrand || "");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [page, setPage] = useState(1);

  const [shuffledProducts, setShuffledProducts] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuWrapRef = useRef(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [netDown, setNetDown] = useState(
    typeof navigator !== "undefined" ? !navigator.onLine : false
  );

  useEffect(() => {
    const onOff = () => setNetDown(true);
    const onOn = () => setNetDown(false);

    window.addEventListener("offline", onOff);
    window.addEventListener("online", onOn);

    return () => {
      window.removeEventListener("offline", onOff);
      window.removeEventListener("online", onOn);
    };
  }, []);

  const isNetworkError = useMemo(() => {
    const msg = String(message || "").toLowerCase();
    return (
      netDown ||
      (typeof navigator !== "undefined" && !navigator.onLine) ||
      msg.includes("network error") ||
      msg.includes("failed to fetch") ||
      msg.includes("err_network") ||
      msg.includes("timeout") ||
      msg.includes("502") ||
      msg.includes("503") ||
      msg.includes("gateway") ||
      msg.includes("service unavailable")
    );
  }, [message, netDown]);

  const isSearching = Boolean((urlSearch || "").trim());
  const fetchLimit = isSearching ? SEARCH_LIMIT : perPage;

  const handleRetry = useCallback(() => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return;
    setNetDown(false);

    const query = {
      brand: selectedBrandTitle || undefined,
      tag: selectedTags?.[0] || undefined,
      category: selectedCategory || undefined,
      minPrice: minPrice || undefined,
      maxPrice: maxPrice || undefined,
      sort: sortBy || undefined,
      searchTerm: urlSearch || undefined,
      page: isSearching ? 1 : page,
      limit: fetchLimit,
    };

    dispatch(getAllProducts(query));
  }, [
    dispatch,
    selectedBrandTitle,
    selectedTags,
    selectedCategory,
    minPrice,
    maxPrice,
    sortBy,
    page,
    fetchLimit,
    isSearching,
    urlSearch,
  ]);

  const [wishToast, setWishToast] = useState({
    show: false,
    type: "success",
    text: "",
  });
  const toastTimerRef = useRef(null);

  const showWishToast = useCallback((text, type = "success") => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);

    setWishToast({
      show: true,
      text: text || "Added to wishlist ✅",
      type,
    });

    toastTimerRef.current = setTimeout(() => {
      setWishToast({ show: false, type: "success", text: "" });
    }, 1800);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  const setQueryParam = useCallback(
    (key, value) => {
      const params = new URLSearchParams(location.search);

      if (value === "" || value == null) params.delete(key);
      else params.set(key, String(value));

      const q = params.toString();
      navigate(`/product${q ? `?${q}` : ""}`);
    },
    [location.search, navigate]
  );

  useEffect(() => {
    setPage(1);
  }, [urlCategory, urlSearch, urlBrand]);

  useEffect(() => {
    setSelectedBrandTitle(urlBrand || "");
  }, [urlBrand]);

  const stars = (count = 0) => {
    const safe = Math.max(0, Math.min(5, Number(count) || 0));
    const full = "★".repeat(safe);
    const empty = "☆".repeat(5 - safe);
    return <span className="os-stars">{full}{empty}</span>;
  };

  const getProductImage = (p) => {
    const imgs = p?.images;

    if (Array.isArray(imgs) && imgs.length > 0) {
      const first = imgs[0];
      if (typeof first === "string") return first;
      if (first?.url) return first.url;
    }

    if (typeof p?.image === "string") return p.image;

    return "https://via.placeholder.com/600x600?text=Kupto";
  };

  const getProductId = (p) => p?._id || p?.id;
  const getProductSlug = (p) => String(p?.slug || "").trim();

  const getColorId = (c) =>
    c?._id || c?.id || String(c?.name || c?.title || "");

  const getColorHex = (c) =>
    c?.hex || c?.code || c?.value || c?.colorCode || c?.colour || "#999";

  const getColorName = (c) => c?.name || c?.title || c?.color || "Color";

  const getBrandId = (b) => b?._id || b?.id;
  const getBrandTitle = (b) => b?.title || b?.name || "Brand";

  const fmtUGX = useCallback((n) => {
    const v = Number(n || 0) || 0;
    return v.toLocaleString();
  }, []);

  const formatDiscountRange = useCallback(
    (now, old) => {
      const a = Number(now || 0) || 0;
      const b = Number(old || 0) || 0;
      const low = Math.min(a, b);
      const high = Math.max(a, b);
      return `UGX(${fmtUGX(low)}–${fmtUGX(high)})`;
    },
    [fmtUGX]
  );

  useEffect(() => {
    dispatch(getAllColors());
    dispatch(getAllBrands());
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn) dispatch(fetchUserCart());
  }, [dispatch, isLoggedIn]);

  useEffect(() => {
    setPage(1);
  }, [
    selectedBrandTitle,
    selectedTags,
    selectedCategory,
    minPrice,
    maxPrice,
    sortBy,
  ]);

  useEffect(() => {
    const query = {
      brand: selectedBrandTitle || undefined,
      tag: selectedTags?.[0] || undefined,
      category: selectedCategory || undefined,
      minPrice: minPrice || undefined,
      maxPrice: maxPrice || undefined,
      sort: sortBy || undefined,
      searchTerm: urlSearch || undefined,
      page: isSearching ? 1 : page,
      limit: fetchLimit,
    };

    dispatch(getAllProducts(query));
  }, [
    dispatch,
    selectedBrandTitle,
    selectedTags,
    selectedCategory,
    minPrice,
    maxPrice,
    sortBy,
    page,
    fetchLimit,
    isSearching,
    urlSearch,
  ]);

  const locallyFilteredProducts = useMemo(() => {
    let list = Array.isArray(products) ? [...products] : [];

    list = list.filter((p) => productHasCategoryId(p, selectedCategory));
    list = list.filter((p) => productHasTag(p, selectedTags));
    list = list.filter((p) => productHasColorId(p, selectedColors));
    list = list.filter((p) => productMatchesPrice(p, minPrice, maxPrice));

    return sortProductsLocal(list, sortBy);
  }, [
    products,
    selectedCategory,
    selectedTags,
    selectedColors,
    minPrice,
    maxPrice,
    sortBy,
  ]);

  useEffect(() => {
    setShuffledProducts(shuffleArray(locallyFilteredProducts));
  }, [locallyFilteredProducts]);

  const didMountScrollRef = useRef(false);
  useEffect(() => {
    if (!didMountScrollRef.current) {
      didMountScrollRef.current = true;
      return;
    }

    if (isSearching) return;
    if (typeof window === "undefined") return;

    setOpenMenuId(null);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [page, isSearching]);

  const didMountBrandScrollRef = useRef(false);
  useEffect(() => {
    if (!didMountBrandScrollRef.current) {
      didMountBrandScrollRef.current = true;
      return;
    }

    if (typeof window === "undefined") return;

    setOpenMenuId(null);
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [selectedBrandTitle]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!openMenuId) return;
      if (menuWrapRef.current && !menuWrapRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };

    const onEsc = (e) => {
      if (e.key === "Escape") setOpenMenuId(null);
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [openMenuId]);

  useEffect(() => {
    if (!filtersOpen) return;

    const onEsc = (e) => {
      if (e.key === "Escape") setFiltersOpen(false);
    };

    document.addEventListener("keydown", onEsc);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", onEsc);
      document.body.style.overflow = prev;
    };
  }, [filtersOpen]);

  const bestSellers = useMemo(() => {
    const list = Array.isArray(locallyFilteredProducts)
      ? locallyFilteredProducts
      : [];

    return [...list]
      .sort((a, b) => Number(b.totalrating || 0) - Number(a.totalrating || 0))
      .slice(0, 3)
      .map((p) => ({
        id: getProductId(p),
        slug: getProductSlug(p),
        title: p?.title || "",
        price: p?.price || 0,
        oldPrice: p?.discountedPrice ? p?.price : p?.price || 0,
        img: getProductImage(p),
        rating: p?.totalrating || 0,
      }));
  }, [locallyFilteredProducts]);

  const onSortChange = (v) => {
    if (v === "position") setSortBy("");
    if (v === "new") setSortBy("-createdAt");
    if (v === "price-low") setSortBy("price");
    if (v === "price-high") setSortBy("-price");
    if (v === "rating") setSortBy("-totalrating");
  };

  const listToRender = Array.isArray(shuffledProducts) ? shuffledProducts : [];

  const totalItems = isSearching
    ? Number(totalFromApi ?? listToRender.length ?? 0) || 0
    : Number(totalFromApi ?? products?.length ?? listToRender.length ?? 0) || 0;

  const totalPages = isSearching
    ? 1
    : Math.max(1, Math.ceil(totalItems / Math.max(1, Number(perPage) || 1)));

  const safePage = clamp(page, 1, totalPages);

  const startIndex = totalItems === 0 ? 0 : (safePage - 1) * perPage + 1;
  const endIndex =
    totalItems === 0
      ? 0
      : isSearching
      ? listToRender.length
      : Math.min(startIndex + listToRender.length - 1, totalItems);

  const pageButtons = useMemo(() => {
    const maxBtns = 5;

    if (totalPages <= maxBtns) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    let start = safePage - 2;
    let end = safePage + 2;

    if (start < 1) {
      start = 1;
      end = 5;
    }

    if (end > totalPages) {
      end = totalPages;
      start = totalPages - 4;
    }

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  }, [safePage, totalPages]);

  const clearAllFilters = useCallback(() => {
    setSelectedBrandTitle("");
    setSelectedTags([]);
    setSelectedColors([]);
    setMinPrice("");
    setMaxPrice("");
    setSortBy("");
    setPage(1);

    const params = new URLSearchParams(location.search);
    params.delete("category");
    params.delete("brand");

    const q = params.toString();
    navigate(`/product${q ? `?${q}` : ""}`);
  }, [location.search, navigate]);

  const activeFiltersCount =
    (selectedCategory ? 1 : 0) +
    (selectedBrandTitle ? 1 : 0) +
    (selectedTags.length ? 1 : 0) +
    (selectedColors.length ? 1 : 0) +
    (minPrice !== "" || maxPrice !== "" ? 1 : 0);

  const readGuestCart = useCallback(() => {
    try {
      const raw = localStorage.getItem(GUEST_CART_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }, []);

  const writeGuestCart = useCallback((items) => {
    try {
      localStorage.setItem(
        GUEST_CART_KEY,
        JSON.stringify(Array.isArray(items) ? items : [])
      );
    } catch {
      // ignore
    }
  }, []);

  const addToGuestCart = useCallback(
    (payload) => {
      const list = readGuestCart();
      const pid = String(payload?.productId || "");

      const idx = list.findIndex((it) => String(it?.productId || "") === pid);

      if (idx >= 0) {
        const prevQty = Number(list[idx]?.quantity || 0);
        const addQty = Number(payload?.quantity || 0);
        const nextQty = Math.max(1, prevQty + Math.max(1, addQty));

        list[idx] = {
          ...list[idx],
          ...payload,
          quantity: nextQty,
        };
      } else {
        list.push({
          ...payload,
          addedAt: new Date().toISOString(),
        });
      }

      writeGuestCart(list);
      window.dispatchEvent(new Event("guestCartUpdated"));
    },
    [readGuestCart, writeGuestCart]
  );

  const productNeedsSelections = useCallback((p) => {
    const sizes = p?.size ?? p?.sizes ?? [];
    const colorsArr = p?.color ?? p?.colors ?? [];

    const hasSizes = Array.isArray(sizes) ? sizes.length > 0 : Boolean(sizes);
    const hasColors = Array.isArray(colorsArr)
      ? colorsArr.length > 0
      : Boolean(colorsArr);

    const tags = Array.isArray(p?.tags) ? p.tags : [];
    const tagSet = new Set(tags.map((t) => String(t).toLowerCase().trim()));
    const hasFileUploadTag =
      tagSet.has("file upload") || tagSet.has("featured file upload");

    const isPrintable = Boolean(p?.isPrintable) || hasFileUploadTag;

    const hasPrintPricing =
      Boolean(p?.printingPrice) ||
      Boolean(p?.printPrice) ||
      Boolean(p?.printPricing) ||
      Boolean(p?.printingType) ||
      Boolean(p?.printType);

    return Boolean(hasSizes || hasColors || isPrintable || hasPrintPricing);
  }, []);

  const onAddToCart = useCallback(
    async (p) => {
      const id = getProductId(p);
      const slug = getProductSlug(p);
      if (!id) return;

      if (productNeedsSelections(p)) {
        if (slug) navigate(`/product/${slug}`);
        return;
      }

      const img = getProductImage(p);
      const nowPrice = Number(p?.discountedPrice ?? p?.price ?? 0) || 0;

      const payload = {
        productId: id,
        quantity: 1,
        price: nowPrice,
        variantImage: img,
      };

      const guestDisplay = {
        name: p?.title || "Product",
        slug: slug || "",
        img,
        colorLabel: "",
        sizeLabel: "",
        oldPrice: p?.discountedPrice ? Number(p?.price ?? 0) : null,
      };

      try {
        if (isLoggedIn) {
          await dispatch(addProdToCart(payload)).unwrap();
          dispatch(fetchUserCart());
        } else {
          addToGuestCart({ ...payload, ...guestDisplay });
        }
      } catch (e) {
        console.log("add to cart error:", e);
      }
    },
    [dispatch, navigate, isLoggedIn, addToGuestCart, productNeedsSelections]
  );

  const onWishlist = useCallback(
    async (p) => {
      const id = getProductId(p);
      if (!id) return;

      try {
        await dispatch(addToWishlists(id)).unwrap();
        showWishToast("Added to wishlist ✅", "success");
      } catch (e) {
        console.log("wishlist error:", e);
        showWishToast("Failed to add to wishlist ❌", "error");
      }
    },
    [dispatch, showWishToast]
  );

  const goToProduct = useCallback(
    (p) => {
      const slug = getProductSlug(p);
      if (!slug) return;
      navigate(`/product/${slug}`);
    },
    [navigate]
  );

  const fmtNowPrice = (p) => Number(p?.discountedPrice ?? p?.price ?? 0) || 0;

  return (
    <>
      <Meta title="Our Store" />

      {wishToast.show && (
        <div
          className={`ku-wish-toast ${wishToast.type || "success"}`}
          role="status"
          aria-live="polite"
        >
          {wishToast.text}
        </div>
      )}

      {isApp ? (
        <section className="os-appTop" aria-label="Store controls">
          <div className="os-appTopInner">
            <button
              type="button"
              className="os-appFilterBtn"
              onClick={() => setFiltersOpen(true)}
              aria-label="Open filters"
            >
              <TbAdjustmentsHorizontal className="os-appFilterIcon" />
              Filters
              {activeFiltersCount > 0 ? (
                <span className="os-appBadge">{activeFiltersCount}</span>
              ) : null}
            </button>

            <div className="os-appSort">
              <select
                value={
                  sortBy === ""
                    ? "position"
                    : sortBy === "-createdAt"
                    ? "new"
                    : sortBy === "price"
                    ? "price-low"
                    : sortBy === "-price"
                    ? "price-high"
                    : sortBy === "-totalrating"
                    ? "rating"
                    : "position"
                }
                onChange={(e) => onSortChange(e.target.value)}
                disabled={isLoading}
                aria-label="Sort products"
              >
                <option value="position">Sort: Position</option>
                <option value="new">Sort: Newest</option>
                <option value="price-low">Sort: Price low</option>
                <option value="price-high">Sort: Price high</option>
                <option value="rating">Sort: Rating</option>
              </select>
            </div>

            <div className="os-appShow">
              <select
                value={perPage}
                onChange={(e) => {
                  setPerPage(Number(e.target.value));
                  setPage(1);
                }}
                disabled={isLoading || isSearching}
                aria-label="Items per page"
                title={
                  isSearching
                    ? "Per-page disabled while searching"
                    : "Items per page"
                }
              >
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </select>
            </div>
          </div>
        </section>
      ) : null}

      {isApp && typeof document !== "undefined"
        ? createPortal(
            <>
              {filtersOpen && (
                <div
                  className="os-appOverlay"
                  onClick={() => setFiltersOpen(false)}
                  aria-hidden="true"
                />
              )}

              <aside
                className={`os-appDrawer ${filtersOpen ? "is-open" : ""}`}
                aria-label="Filters drawer"
              >
                <div className="os-appDrawerHead">
                  <div className="os-appDrawerTitle">Filters</div>
                  <button
                    type="button"
                    className="os-appClose"
                    onClick={() => setFiltersOpen(false)}
                    aria-label="Close filters"
                  >
                    <IoClose />
                  </button>
                </div>

                {isLoading || brandsLoading ? (
                  <AppDrawerSkeleton />
                ) : (
                  <div className="os-appDrawerBody">
                    <div className="os-appBlock">
                      <div className="os-appBlockTitle">Category</div>
                      <div className="os-checklist os-checklist--app">
                        {(categories?.length ? categories : []).map((c) => (
                          <label className="os-checkrow" key={c._id}>
                            <input
                              type="radio"
                              name="category_app"
                              checked={selectedCategory === c._id}
                              onChange={() => {
                                setQueryParam("category", c._id);
                              }}
                            />
                            <span>{c.title}</span>
                          </label>
                        ))}

                        {selectedCategory && (
                          <button
                            type="button"
                            className="os-btnClear"
                            onClick={() => setQueryParam("category", "")}
                          >
                            Clear Category
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="os-sep" />

                    <div className="os-appBlock">
                      <div className="os-appBlockTitle">Price</div>
                      <div className="os-priceRow os-priceRow--app">
                        <input
                          className="os-inp"
                          placeholder="Min"
                          value={minPrice}
                          onChange={(e) => setMinPrice(e.target.value)}
                        />
                        <input
                          className="os-inp"
                          placeholder="Max"
                          value={maxPrice}
                          onChange={(e) => setMaxPrice(e.target.value)}
                        />
                        <button
                          className="os-btnSearch"
                          type="button"
                          onClick={() => setFiltersOpen(false)}
                        >
                          APPLY
                        </button>
                      </div>
                    </div>

                    <div className="os-sep" />

                    <div className="os-appBlock">
                      <div className="os-appBlockTitle">Brand</div>
                      <div className="os-checklist os-checklist--app">
                        {brands.map((b) => {
                          const id = getBrandId(b);
                          const title = getBrandTitle(b);
                          const checked = selectedBrandTitle === title;

                          return (
                            <label className="os-checkrow" key={id}>
                              <input
                                type="radio"
                                name="brand_app"
                                checked={checked}
                                onChange={() => {
                                  setSelectedBrandTitle(title || "");
                                  setQueryParam("brand", title || "");
                                  setPage(1);
                                }}
                              />
                              <span>{title}</span>
                            </label>
                          );
                        })}

                        {brands.length === 0 && (
                          <div style={{ fontSize: 12, color: "#777" }}>
                            No brands loaded
                          </div>
                        )}

                        {selectedBrandTitle && (
                          <button
                            type="button"
                            className="os-btnClear"
                            onClick={() => {
                              setSelectedBrandTitle("");
                              setQueryParam("brand", "");
                            }}
                          >
                            Clear Brand
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="os-sep" />

                    <div className="os-appBlock">
                      <div className="os-appBlockTitle">Tags</div>
                      <div className="os-checklist os-checklist--app">
                        {[
                          "featured",
                          "File Upload",
                          "featured File Upload",
                          "others",
                        ].map((t) => (
                          <label className="os-checkrow" key={t}>
                            <input
                              type="checkbox"
                              checked={selectedTags.includes(t)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSelectedTags((prev) =>
                                  checked
                                    ? [...prev, t]
                                    : prev.filter((x) => x !== t)
                                );
                              }}
                            />
                            <span>{t}</span>
                          </label>
                        ))}

                        {selectedTags.length > 0 && (
                          <button
                            type="button"
                            className="os-btnClear"
                            onClick={() => setSelectedTags([])}
                          >
                            Clear Tags
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="os-sep" />

                    <div className="os-appBlock">
                      <div className="os-appBlockTitle">Color</div>
                      <div className="os-colorGrid os-colorGrid--app">
                        {(Array.isArray(colors) ? colors : []).map((c) => {
                          const id = getColorId(c);
                          const hex = getColorHex(c);
                          const active = selectedColors.includes(id);

                          return (
                            <button
                              key={id}
                              type="button"
                              className={`os-colorDot ${
                                active ? "active" : ""
                              }`}
                              title={getColorName(c)}
                              style={{ background: hex }}
                              onClick={() => {
                                setSelectedColors((prev) =>
                                  prev.includes(id)
                                    ? prev.filter((x) => x !== id)
                                    : [...prev, id]
                                );
                              }}
                              aria-label={getColorName(c)}
                              aria-pressed={active}
                            />
                          );
                        })}
                      </div>

                      {selectedColors.length > 0 && (
                        <button
                          type="button"
                          className="os-btnClear"
                          onClick={() => setSelectedColors([])}
                          style={{ marginTop: 10 }}
                        >
                          Clear Colors
                        </button>
                      )}
                    </div>

                    <div className="os-appDrawerFoot">
                      <button
                        type="button"
                        className="os-appGhost"
                        onClick={clearAllFilters}
                      >
                        Clear all
                      </button>
                      <button
                        type="button"
                        className="os-appPrimary"
                        onClick={() => setFiltersOpen(false)}
                      >
                        Show results
                      </button>
                    </div>
                  </div>
                )}
              </aside>
            </>,
            document.body
          )
        : null}

      {!isLoading && isError && isNetworkError ? (
        <OfflineOverlay onRetry={handleRetry} />
      ) : (
        <Container class1="os-wrap">
          <div className={`os-shell ${isApp ? "os-shell--app" : ""}`}>
            {!isApp ? (
              <aside className="os-sidebar">
                {isLoading || brandsLoading ? (
                  <SidebarSkeleton />
                ) : (
                  <>
                    <div className="os-panel">
                      <div className="os-panel-head">SHOP BY</div>

                      <div className="os-block">
                        <div className="os-block-title">CATEGORY</div>
                        <div className="os-checklist">
                          {(categories?.length ? categories : []).map((c) => (
                            <label className="os-checkrow" key={c._id}>
                              <input
                                type="radio"
                                name="category"
                                checked={selectedCategory === c._id}
                                onChange={() => {
                                  setQueryParam("category", c._id);
                                }}
                              />
                              <span>{c.title}</span>
                            </label>
                          ))}

                          {categories?.length === 0 && (
                            <div style={{ fontSize: 12, color: "#777" }}>
                              No categories loaded
                            </div>
                          )}

                          {selectedCategory && (
                            <button
                              type="button"
                              className="os-btnClear"
                              onClick={() => setQueryParam("category", "")}
                              style={{ marginTop: 10 }}
                            >
                              Clear Category
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="os-sep" />

                      <div className="os-block">
                        <div className="os-block-title">PRICE</div>
                        <div className="os-priceRow">
                          <input
                            className="os-inp"
                            placeholder="Min"
                            value={minPrice}
                            onChange={(e) => setMinPrice(e.target.value)}
                          />
                          <input
                            className="os-inp"
                            placeholder="Max"
                            value={maxPrice}
                            onChange={(e) => setMaxPrice(e.target.value)}
                          />
                          <button className="os-btnSearch" type="button">
                            SEARCH
                          </button>
                        </div>
                      </div>

                      <div className="os-sep" />

                      <div className="os-block">
                        <div className="os-block-title">BRAND</div>
                        <div className="os-checklist">
                          {brands.map((b) => {
                            const id = getBrandId(b);
                            const title = getBrandTitle(b);
                            const checked = selectedBrandTitle === title;

                            return (
                              <label className="os-checkrow" key={id}>
                                <input
                                  type="radio"
                                  name="brand"
                                  checked={checked}
                                  onChange={() => {
                                    setSelectedBrandTitle(title || "");
                                    setQueryParam("brand", title || "");
                                    setPage(1);
                                  }}
                                />
                                <span>{title}</span>
                              </label>
                            );
                          })}

                          {brands.length === 0 && (
                            <div style={{ fontSize: 12, color: "#777" }}>
                              No brands loaded
                            </div>
                          )}

                          {selectedBrandTitle && (
                            <button
                              type="button"
                              className="os-btnClear"
                              onClick={() => {
                                setSelectedBrandTitle("");
                                setQueryParam("brand", "");
                              }}
                              style={{ marginTop: 10 }}
                            >
                              Clear Brand
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="os-sep" />

                      <div className="os-block">
                        <div className="os-block-title">TAGS</div>
                        <div className="os-checklist">
                          {[
                            "featured",
                            "File Upload",
                            "featured File Upload",
                            "others",
                          ].map((t) => (
                            <label className="os-checkrow" key={t}>
                              <input
                                type="checkbox"
                                checked={selectedTags.includes(t)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setSelectedTags((prev) =>
                                    checked
                                      ? [...prev, t]
                                      : prev.filter((x) => x !== t)
                                  );
                                }}
                              />
                              <span>{t}</span>
                            </label>
                          ))}

                          {selectedTags.length > 0 && (
                            <button
                              type="button"
                              className="os-btnClear"
                              onClick={() => setSelectedTags([])}
                              style={{ marginTop: 10 }}
                            >
                              Clear Tags
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="os-sep" />

                      <div className="os-block">
                        <div className="os-block-title">COLOR</div>

                        <div className="os-colorGrid">
                          {(Array.isArray(colors) ? colors : []).map((c) => {
                            const id = getColorId(c);
                            const hex = getColorHex(c);
                            const active = selectedColors.includes(id);

                            return (
                              <button
                                key={id}
                                type="button"
                                className={`os-colorDot ${
                                  active ? "active" : ""
                                }`}
                                title={getColorName(c)}
                                style={{ background: hex }}
                                onClick={() => {
                                  setSelectedColors((prev) =>
                                    prev.includes(id)
                                      ? prev.filter((x) => x !== id)
                                      : [...prev, id]
                                  );
                                }}
                                aria-label={getColorName(c)}
                                aria-pressed={active}
                              />
                            );
                          })}

                          {(!colors || colors.length === 0) && (
                            <div style={{ fontSize: 12, color: "#777" }}>
                              No colors loaded.
                            </div>
                          )}
                        </div>

                        {selectedColors.length > 0 && (
                          <button
                            type="button"
                            className="os-btnClear"
                            onClick={() => setSelectedColors([])}
                            style={{ marginTop: 10 }}
                          >
                            Clear Colors
                          </button>
                        )}
                      </div>
                    </div>

                    <div className="os-panel">
                      <div className="os-panel-head">BEST SELLERS</div>
                      <div className="os-bestWrap">
                        {bestSellers.map((p) => (
                          <Link
                            to={`/product/${p.slug}`}
                            className="os-bestItem"
                            key={p.id}
                          >
                            <div className="os-bestImg">
                              <img src={p.img} alt={p.title} />
                            </div>
                            <div className="os-bestInfo">
                              <div className="os-bestTitle">{p.title}</div>
                              <div className="os-bestStars">{stars(p.rating)}</div>
                              <div className="os-bestPrice">
                                <span className="os-now">
                                  UGX {Number(p.price || 0).toLocaleString()}
                                </span>
                              </div>
                            </div>
                          </Link>
                        ))}

                        {bestSellers.length === 0 && (
                          <div
                            style={{
                              fontSize: 12,
                              color: "#777",
                              padding: "10px 0",
                            }}
                          >
                            No best sellers yet.
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </aside>
            ) : null}

            <main className="os-main">
              {!isApp ? (
                <div className="os-toolbar">
                  <div className="os-toolbarLeft">
                    <div className="os-tool">
                      <span>Sort By</span>
                      <select
                        value={
                          sortBy === ""
                            ? "position"
                            : sortBy === "-createdAt"
                            ? "new"
                            : sortBy === "price"
                            ? "price-low"
                            : sortBy === "-price"
                            ? "price-high"
                            : sortBy === "-totalrating"
                            ? "rating"
                            : "position"
                        }
                        onChange={(e) => onSortChange(e.target.value)}
                        disabled={isLoading}
                      >
                        <option value="position">Position</option>
                        <option value="new">Newest</option>
                        <option value="price-low">Price: Low to High</option>
                        <option value="price-high">Price: High to Low</option>
                        <option value="rating">Rating</option>
                      </select>
                    </div>

                    <div className="os-tool">
                      <span>Show</span>
                      <select
                        value={perPage}
                        onChange={(e) => {
                          setPerPage(Number(e.target.value));
                          setPage(1);
                        }}
                        disabled={isLoading || isSearching}
                        title={
                          isSearching
                            ? "Per-page disabled while searching"
                            : "Items per page"
                        }
                      >
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={500}>500</option>
                        <option value={1000}>1000</option>
                      </select>
                    </div>

                    {isSearching ? (
                      <div
                        style={{
                          marginLeft: 12,
                          fontSize: 12,
                          color: "#777",
                        }}
                      >
                        Showing all matches for: <b>{urlSearch}</b>
                      </div>
                    ) : null}
                  </div>

                  <div className="os-toolbarRight">
                    <button
                      className="os-view active"
                      type="button"
                      disabled={isLoading}
                    >
                      ▦
                    </button>
                    <button
                      className="os-view"
                      type="button"
                      disabled={isLoading}
                    >
                      ☰
                    </button>
                  </div>
                </div>
              ) : null}

              {isLoading && <SkeletonGrid count={Math.min(perPage, 24)} />}

              {!isLoading && isError && (
                <p style={{ padding: "10px 0", color: "crimson" }}>
                  {message || "Failed to load products"}
                </p>
              )}

              {!isLoading && !isError && (
                <div className={`os-grid ${isApp ? "os-grid--app" : ""}`}>
                  {listToRender.map((p) => {
                    const id = getProductId(p);
                    const slug = getProductSlug(p);
                    const isOpen = openMenuId === id;
                    const img = getProductImage(p);

                    const hasDiscount = Boolean(p?.discountedPrice);
                    const oldPrice = hasDiscount
                      ? Number(p?.price ?? 0)
                      : null;
                    const nowPrice = fmtNowPrice(p);
                    const ratingNum = Number(p?.totalrating || 0);

                    const priceText = hasDiscount
                      ? formatDiscountRange(nowPrice, oldPrice)
                      : `UGX ${fmtUGX(nowPrice)}`;

                    return (
                      <article
                        key={id}
                        className="trend-card"
                        role="button"
                        tabIndex={0}
                        onClick={() => goToProduct(p)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") goToProduct(p);
                        }}
                      >
                        {!isApp ? (
                          <div
                            className="os-menuWrap trend-menuWrap"
                            ref={isOpen ? menuWrapRef : null}
                          >
                            <button
                              type="button"
                              className="os-cornerBtn os-menuBtn"
                              aria-haspopup="menu"
                              aria-expanded={isOpen ? "true" : "false"}
                              title="Options"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(isOpen ? null : id);
                              }}
                            >
                              ☰
                            </button>

                            {isOpen && (
                              <div
                                className="os-dropdown"
                                role="menu"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className="os-dropItem"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onWishlist(p);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  <span className="os-dropIcon">
                                    <IoIosHeartEmpty />
                                  </span>
                                  <span>Favourite</span>
                                </button>

                                <button
                                  type="button"
                                  className="os-dropItem"
                                  role="menuitem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onAddToCart(p);
                                    setOpenMenuId(null);
                                  }}
                                >
                                  <span className="os-dropIcon">🛒</span>
                                  <span>Add to Cart</span>
                                </button>
                              </div>
                            )}
                          </div>
                        ) : null}

                        <div
                          className="trend-img-wrap"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Link
                            to={`/product/${slug}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <ImageWithPlaceholder src={img} alt={p?.title} />
                          </Link>
                        </div>

                        <div className="hp-popBody1">
                          <div className="hp-popTitleRow1">
                            <div className="hp-popTitle1" title={p?.title}>
                              {p?.title}
                            </div>

                            <div className="hp-popRating1">
                              <span className="hp-popStarIcon1">★</span>
                              <span className="hp-popRateText1">
                                ({ratingNum.toFixed(1)})
                              </span>
                            </div>
                          </div>

                          <div className="hp-popPrices1">
                            <span className="hp-popNow1">{priceText}</span>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )}

              {!isLoading && !isError && (
                <div className="os-footerbar">
                  <div
                    className="os-toolbarLeft"
                    style={{ alignItems: "center" }}
                  >
                    <div style={{ fontSize: 12, color: "#777" }}>
                      {isSearching ? (
                        <>
                          Showing {totalItems} results for <b>{urlSearch}</b>
                        </>
                      ) : (
                        <>
                          Showing {startIndex}–{endIndex} of {totalItems}
                        </>
                      )}
                    </div>
                  </div>

                  {!isSearching && totalPages > 1 && (
                    <div className="os-pages">
                      <button
                        type="button"
                        onClick={() =>
                          setPage((prev) => clamp(prev - 1, 1, totalPages))
                        }
                        disabled={safePage === 1}
                        title="Previous"
                      >
                        ‹
                      </button>

                      {pageButtons.map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => setPage(n)}
                          className={safePage === n ? "active" : ""}
                        >
                          {n}
                        </button>
                      ))}

                      <button
                        type="button"
                        onClick={() =>
                          setPage((prev) => clamp(prev + 1, 1, totalPages))
                        }
                        disabled={safePage === totalPages}
                        title="Next"
                      >
                        ›
                      </button>
                    </div>
                  )}
                </div>
              )}
            </main>
          </div>
        </Container>
      )}
    </>
  );
};

export default OurStore;